import { ShowAddCardFunc } from "src/features/paymentGateways/types";
import {
    getToastAddedCardOperation,
    showToastAddCardOperation,
} from "src/features/paymentGateways/pos/toast/operations";
import {
    ToastInitializationFailedEvent,
    ToastInitializationSucceededEvent,
    ToastInputChangedEvent,
    ToastInputChangedEventContent,
    ToastPaymentMethod,
    ToastPaymentSessionData,
} from "src/features/paymentGateways/pos/toast/types";
import { HostedField, hostedFieldOptions } from "src/features/paymentMethods/types";
import { loadExternalScript, timeoutAndError } from "src/common/shared";
import { actionCreators as paymentActionCreators, AddedCardResult, GetAddedCardFunc } from "src/features/payment";
import { hostedFieldsActionCreators } from "src/features/paymentMethods";
import { AddedCardPaymentMethod, PaymentMethodVisibility, PaymentType } from "src/common/payment";
import { fetchPaymentSession } from "src/features/payment/api/fetchPaymentSession";
import { fetchPaymentMethodSession } from "src/features/paymentMethods/api/fetchPaymentMethodSession";
import { getInParty } from "src/features/order/selectors";
import { getMenuData } from "src/features/menu/selectors";
import {
    getApplePayPaymentMethod,
    getGooglePayPaymentMethod,
    getPaymentMethods,
    getSelectedPaymentMethod,
} from "src/features/payment/selectors";
import { getSelectablePaymentMethod } from "src/features/payment/util/getSelectablePaymentMethod";
import { createToastPaymentMethod } from "src/features/paymentGateways/pos/toast/actions/createToastPaymentMethod";
import { confirmToastIntent } from "src/features/paymentGateways/pos/toast/actions/confirmToastIntent";
import { getCardDisplayName } from "../../../../payment/util/getCardDisplayName";
import { showModalMessage } from "src/features/modalMessage/actions/show";
import { modalMessages } from "src/features/modalMessage/messages";

export const showToastAddCard: ShowAddCardFunc = (dispatch, getState, newCardPaymentMethod, isPayment) => {
    return showToastAddCardOperation.invoke(async () => {
        const state = getState();
        const inParty = getInParty(state);
        const menuData = getMenuData(state);

        const paymentSessionData = isPayment
            ? await fetchPaymentSession<ToastPaymentSessionData>(dispatch, newCardPaymentMethod.paymentGateway, inParty)
            : await fetchPaymentMethodSession<ToastPaymentSessionData>(newCardPaymentMethod);

        if (!paymentSessionData) {
            throw new Error("No payment session data");
        }

        dispatch(paymentActionCreators.setPaymentSessionData(paymentSessionData));

        const { baseUrl, oauthToken, sessionSecret, merchantId, acceptAmex } = paymentSessionData;

        let selectedPaymentMethod: ToastPaymentMethod | undefined;
        let useLastResult = false;
        let isOpenTabContext = false;

        const onInputChanged = (content: ToastInputChangedEventContent) => {
            let isValid: boolean;
            ({ isValid, selectedPaymentMethod } = content);

            // Show unsupported message for Apple Pay/Google Pay in open tab context
            if (
                isOpenTabContext &&
                (selectedPaymentMethod === "APPLE_PAY" || selectedPaymentMethod === "GOOGLE_PAY")
            ) {
                dispatch(showModalMessage(modalMessages.unsupportedPaymentMethodForOpenTab()));
                // Mark as invalid to prevent proceeding with unsupported payment method
                dispatch(hostedFieldsActionCreators.setValid(HostedField.Number, false));
                dispatch(hostedFieldsActionCreators.setValid(HostedField.Expiry, false));
                dispatch(hostedFieldsActionCreators.setValid(HostedField.CVV, false));
                useLastResult = false;
                return;
            }

            dispatch(hostedFieldsActionCreators.setValid(HostedField.Number, isValid));
            dispatch(hostedFieldsActionCreators.setValid(HostedField.Expiry, isValid));
            dispatch(hostedFieldsActionCreators.setValid(HostedField.CVV, isValid));
            useLastResult = false;
        };

        const initializeHostedCheckout = () =>
            new Promise<any>(async (resolve, reject) => {
                try {
                    const Toast = await loadExternalScript("Toast", `${baseUrl}/assets/loader.js`);

                    // Determine which container ID to use based on which div exists and is visible
                    let targetContainerId = "toast-container-card";
                    const openTabContainer = document.getElementById("toast-container-open-tab");
                    const cardContainer = document.getElementById("toast-container-card");

                    // Check which container is actually visible (not display:none or hidden by parent)
                    const isVisible = (el: HTMLElement | null): boolean => {
                        if (!el) return false;
                        const style = window.getComputedStyle(el);
                        if (style.display === "none" || style.visibility === "hidden") return false;
                        // Check if parent containers are visible
                        let parent = el.parentElement;
                        while (parent) {
                            const parentStyle = window.getComputedStyle(parent);
                            if (parentStyle.display === "none" || parentStyle.visibility === "hidden") return false;
                            parent = parent.parentElement;
                        }
                        return true;
                    };

                    const openTabVisible = isVisible(openTabContainer);
                    const cardVisible = isVisible(cardContainer);

                    if (openTabVisible && !cardVisible) {
                        targetContainerId = "toast-container-open-tab";
                        isOpenTabContext = true;
                    } else if (cardVisible && !openTabVisible) {
                        targetContainerId = "toast-container-card";
                    } else if (openTabContainer && !cardContainer) {
                        targetContainerId = "toast-container-open-tab";
                        isOpenTabContext = true;
                    } else {
                        // Default to card container
                        targetContainerId = "toast-container-card";
                    }

                    // Check if this specific container already has the Toast iframe
                    const existingToastIframe = document.querySelector(`#${targetContainerId} iframe`);
                    if (existingToastIframe) {
                        if (Toast?.monitor) {
                            Toast.monitor((e: ToastInputChangedEvent) => {
                                onInputChanged(e.content);
                            });
                        }

                        resolve(Toast);
                        return;
                    }

                    Toast.initialize(
                        (e: ToastInitializationSucceededEvent) => {
                            onInputChanged(e.content);
                            Toast.monitor((e: ToastInputChangedEvent) => {
                                onInputChanged(e.content);
                            });
                            resolve(Toast);
                        },
                        (e: ToastInitializationFailedEvent) => {
                            reject(new Error(e.cause?.message ?? "Failed to initialize Toast hosted checkout"));
                        },
                        {
                            merchantId,
                            oauthToken,
                            sessionSecret,
                            domElementId: targetContainerId,
                            acceptAmex: acceptAmex === "true",
                        },
                        {
                            backgroundColor: "#ffffff",
                            color: hostedFieldOptions.color,
                            iconColor: menuData?.themeColor,
                            fontFamily: hostedFieldOptions.fontFamily,
                            fontSize: hostedFieldOptions.fontSize,
                            fontWeight: hostedFieldOptions.fontWeight,
                        }
                    );
                } catch (err) {
                    reject(err);
                }
            });

        await Promise.race([
            initializeHostedCheckout(),
            timeoutAndError(hostedFieldOptions.initializeTimeout, "Timed out initializing Toast hosted checkout"),
        ]);

        const getAddedCard: GetAddedCardFunc = () =>
            getToastAddedCardOperation.invoke(async () => {
                const state = getState();

                if (useLastResult) {
                    return getSelectedPaymentMethod(state);
                }

                useLastResult = true;

                switch (selectedPaymentMethod) {
                    case "NEW_CARD":
                        const { paymentMethodId, card } = await createToastPaymentMethod();

                        if (!isPayment) {
                            await confirmToastIntent();
                            window.location.reload();
                        }

                        const { lastFour, expiry, brand } = card ?? {};

                        const addedCardPaymentMethod: AddedCardPaymentMethod = {
                            ...newCardPaymentMethod,
                            paymentType: PaymentType.ADDEDCARD,
                            displayName: brand ? getCardDisplayName(brand) : "New debit or credit card",
                            token: paymentMethodId,
                            maskedNumber: lastFour ? `•••• ${lastFour}` : "New debit or credit card",
                            expirationDate: expiry ? `${expiry.month}/${expiry.year}` : "",
                            productId: "",
                            processingFee: 0,
                            processingFeeBaseAmount: 0,
                            remember: false,
                            visibility: PaymentMethodVisibility.None,
                        };

                        const result: AddedCardResult = {
                            paymentMethod: addedCardPaymentMethod,
                        };

                        return result;
                    case "APPLE_PAY":
                        // Call Toast.createPaymentMethod for Apple/Google Pay when we initialize
                        // the payment button as this triggers the payment sheet for these methods.
                        return getApplePayPaymentMethod(state);
                    case "GOOGLE_PAY":
                        return getGooglePayPaymentMethod(state);
                    case "SAVED_CARD":
                        // We need the token for saved cards so we can match them up with the
                        // card payment method we fetched for a better user experience
                        try {
                            const { paymentMethodId } = await createToastPaymentMethod();
                            const paymentMethods = getPaymentMethods(state);
                            return getSelectablePaymentMethod(paymentMethods, paymentMethodId);
                        } catch (err) {
                            useLastResult = false;
                            throw err;
                        }
                    default:
                        throw new Error("Invalid payment method");
                }
            }, dispatch);

        return getAddedCard;
    }, dispatch);
};
